import React from 'react';
import { Box, Paper, Typography, Fade, useTheme, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GavelIcon from '@mui/icons-material/Gavel';
import QuizIcon from '@mui/icons-material/Quiz';
import TheatersIcon from '@mui/icons-material/Theaters';
import BrushIcon from '@mui/icons-material/Brush';
import PeopleIcon from '@mui/icons-material/People';
import CasinoIcon from '@mui/icons-material/Casino';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { useGame } from '../contexts/GameContext';
import RuleRound from './rounds/RuleRound';
import QuestionRound from './rounds/QuestionRound';
import PantomimeRound from './rounds/PantomimeRound';
import DrawRound from './rounds/DrawRound';
import WhoHasRound from './rounds/WhoHasRound';
import RandomEventRound from './rounds/RandomEventRound';
import DuelRound from './rounds/DuelRound';
import AufzaehlungRound from './rounds/AufzaehlungRound';
import ActiveRules from './ActiveRules';
import DrinkingBuddies from './DrinkingBuddies';

// Import other round components here as they are created

const roundComponents = {
  REGEL: RuleRound,
  FRAGE: QuestionRound,
  PANTOMIME: PantomimeRound,
  ZEICHNEN: DrawRound,
  WER_HAT: WhoHasRound,
  ZUFALLSEREIGNIS: RandomEventRound,
  DUELL: DuelRound,
  AUFZAEHLUNG: AufzaehlungRound
};

const roundTitles = {
  REGEL: 'Regel',
  FRAGE: 'Trivia',
  PANTOMIME: 'Pantomime',
  ZEICHNEN: 'Zeichnen',
  WER_HAT: 'Wer hat...',
  ZUFALLSEREIGNIS: 'Zufallsereignis',
  DUELL: 'Duell',
  AUFZAEHLUNG: 'Aufzählung & Reime'
};

const roundIcons = {
  REGEL: GavelIcon,
  FRAGE: QuizIcon,
  PANTOMIME: TheatersIcon,
  ZEICHNEN: BrushIcon,
  WER_HAT: PeopleIcon,
  ZUFALLSEREIGNIS: CasinoIcon,
  DUELL: SportsKabaddiIcon,
  AUFZAEHLUNG: FormatListBulletedIcon
};

const roundColors = {
  REGEL: '#4caf50',
  FRAGE: '#2196f3',
  PANTOMIME: '#ff9800',
  ZEICHNEN: '#9c27b0',
  WER_HAT: '#9c27b0',
  ZUFALLSEREIGNIS: '#f44336',
  DUELL: '#ff9800',
  AUFZAEHLUNG: '#2196f3'
};

export default function GameBoard() {
  const { state } = useGame();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const getNextPlayer = () => {
    const currentPlayerIndex = state.players.findIndex(p => p.name === state.activePlayer);
    const nextPlayerIndex = (currentPlayerIndex + 1) % state.players.length;
    return state.players[nextPlayerIndex]?.name;
  };

  const getCurrentRound = () => {
    const RoundComponent = roundComponents[state.currentRoundType];
    return <RoundComponent />;
  };

  const RoundIcon = roundIcons[state.currentRoundType];

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto',
      p: 0,
      mb: '60px' // Platz für den Footer
    }}>
      <Fade in timeout={800}>
        <Paper 
          sx={{ 
            p: isMobile ? 1.5 : 2.5,
            mb: isMobile ? 1 : 2,
            boxShadow: 3,
            borderRadius: '1rem',
          }}
        >
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'start',
            gap: isMobile ? 1 : 2
          }}>
            {/* Left - Round number */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: isMobile ? '0.8rem' : '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              Runde {state.currentRound + 1}
            </Typography>

            {/* Center - Players */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"}
                sx={{ 
                  fontWeight: 'bold',
                  color: 'secondary.main',
                  fontSize: isMobile ? '1.5rem' : '1.5rem',
                  textAlign: 'center'
                }}
              >
                {state.activePlayer}
              </Typography>
              
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
                left: '-8px'
              }}>
                <ArrowForwardIcon 
                  sx={{ 
                    fontSize: isMobile ? '0.8rem' : '1.3rem',
                    color: 'text.secondary'
                  }}
                />
                <Typography 
                  variant={isMobile ? "body1" : "h6"}
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: isMobile ? '0.8rem' : '1.1rem'
                  }}
                >
                  {getNextPlayer()}
                </Typography>
              </Box>
            </Box>

            {/* Right - Round type */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              justifyContent: 'flex-end'
            }}>
              <RoundIcon sx={{ 
                fontSize: isMobile ? '1rem' : '1.3rem',
                color: roundColors[state.currentRoundType]
              }} />
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{ 
                  fontWeight: 'bold',
                  color: roundColors[state.currentRoundType],
                  fontSize: isMobile ? '0.7rem' : '1rem'
                }}
              >
                {roundTitles[state.currentRoundType]}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Fade>

      <Fade in timeout={1000}>
        <Paper 
          sx={{ 
            p: isMobile ? 1.5 : 2.5,
            boxShadow: 3,
            borderRadius: '1rem',
            '&:hover': {
              boxShadow: 6,
            },
          }}
        >
          {getCurrentRound()}
        </Paper>
      </Fade>

      <ActiveRules />
      <DrinkingBuddies />
    </Box>
  );
} 